import { existsSync, promises as fs } from 'node:fs';
import { consola } from 'consola';
import {
  containerConfigPath,
  containerConfigsDir,
  containerDir,
  monocerosHome as defaultMonocerosHome,
} from '../config/paths.js';
import { markUpgraded } from '../config/machine-state.js';
import { compareRuntimeVersions, SERVICE_CATALOG } from '../create/catalog.js';
import { isWorkspaceRunning, spawnDocker } from '../devcontainer/compose.js';
import { type DockerExec } from '../proxy/index.js';
import { pruneStaleImages } from './prune.js';
import {
  runApply,
  type RunApplyOptions,
  type RunApplyResult,
} from '../apply/index.js';

/**
 * `monoceros upgrade` — change the pinned runtime image version of a
 * container and re-apply. The image version is part of the container's
 * yml (ADR 0017); routine `apply` never bumps it, so this is the one
 * deliberate path that does.
 *
 *   monoceros upgrade <name>            → pin to the latest published version
 *   monoceros upgrade <name> <version>  → pin to an exact version
 *   monoceros upgrade --list            → list available versions, change nothing
 */

const RUNTIME_REPO = 'getmonoceros/monoceros-runtime';
const VERSION_RE = /^\d+\.\d+\.\d+$/;

export type VersionFetcher = () => Promise<string[]>;

interface UpgradeLogger {
  info: (msg: string) => void;
  success: (msg: string) => void;
  warn?: (msg: string) => void;
}

export interface UpgradeOptions {
  /** Container/config name. Required unless `list` is set. */
  name?: string;
  /** Exact version to pin. Omitted → resolve the latest published version. */
  version?: string;
  /** List available versions and exit without changing anything. */
  list?: boolean;
  cliVersion: string;
  monocerosHome?: string;
  logger?: UpgradeLogger;
  /** Clock seam for the last-upgrade timestamp. */
  now?: Date;
  /** Test seam: resolve the set of available versions (sorted, latest last). */
  fetchVersions?: VersionFetcher;
  /** Test seam: the apply runner (defaults to the real `runApply`). */
  applyRunner?: (opts: RunApplyOptions) => Promise<RunApplyResult>;
  /** Test seam: docker exec used by the image prune. */
  dockerExec?: DockerExec;
}

/**
 * List the published runtime image versions from GHCR. The image is
 * public, so no credentials are needed — but the OCI registry still
 * requires a bearer token, which GHCR hands out anonymously. Filtered
 * to exact `major.minor.patch` tags and sorted ascending (latest last).
 */
export async function fetchRuntimeVersions(): Promise<string[]> {
  const tokenUrl = `https://ghcr.io/token?service=ghcr.io&scope=repository:${RUNTIME_REPO}:pull`;
  const tokenRes = await fetch(tokenUrl);
  if (!tokenRes.ok) {
    throw new Error(
      `Could not get a GHCR token (HTTP ${tokenRes.status}). Pass an explicit version to skip the lookup: \`monoceros upgrade <name> <version>\`.`,
    );
  }
  const { token } = (await tokenRes.json()) as { token?: string };
  if (!token) throw new Error('GHCR token response contained no token.');
  const tagsRes = await fetch(`https://ghcr.io/v2/${RUNTIME_REPO}/tags/list`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!tagsRes.ok) {
    throw new Error(
      `Could not list runtime versions (HTTP ${tagsRes.status}). Pass an explicit version: \`monoceros upgrade <name> <version>\`.`,
    );
  }
  const { tags } = (await tagsRes.json()) as { tags?: string[] };
  return (tags ?? [])
    .filter((t) => VERSION_RE.test(t))
    .sort(compareRuntimeVersions);
}

/**
 * Set `runtimeVersion` in a yml document, preserving everything else.
 * Replaces an existing line, inserts after `schemaVersion:` when
 * absent, or prepends as a last resort. Pure — no I/O.
 */
export function setRuntimeVersion(yml: string, version: string): string {
  if (/^runtimeVersion:.*$/m.test(yml)) {
    return yml.replace(/^runtimeVersion:.*$/m, `runtimeVersion: ${version}`);
  }
  if (/^schemaVersion:.*$/m.test(yml)) {
    return yml.replace(
      /^(schemaVersion:.*)$/m,
      `$1\nruntimeVersion: ${version}`,
    );
  }
  return `runtimeVersion: ${version}\n${yml}`;
}

/**
 * The repository half of an image reference: everything before the tag
 * or digest. A registry port (`ghcr.io:5000/x/app`) is not a tag, so the
 * split is only a tag when it comes after the last `/`.
 */
function imageRepo(image: string): string {
  const repo = image.split('@')[0]!;
  const colon = repo.lastIndexOf(':');
  return colon > repo.lastIndexOf('/') ? repo.slice(0, colon) : repo;
}

/** repo → the image reference the catalog ships today. */
function catalogImagesByRepo(): Map<string, string> {
  const byRepo = new Map<string, string>();
  for (const entry of Object.values(SERVICE_CATALOG)) {
    byRepo.set(imageRepo(entry.image), entry.image);
  }
  return byRepo;
}

/**
 * Pull the curated services in a yml document up to the tags the catalog
 * ships now, preserving comments and everything else (ADR 0052).
 *
 * Matched by image REPOSITORY, not by service name: that also catches an
 * instance renamed with `add-service … --as`, and it leaves an image the
 * builder chose themselves alone, because no catalog entry claims its
 * repository. Only indented `image:` lines are considered, so a
 * top-level key or a commented-out example is never touched.
 *
 * Pure — no I/O. Returns the new document and one `old → new` line per
 * rewrite, for the caller to log.
 */
export function setCuratedServiceImages(yml: string): {
  yml: string;
  changed: string[];
} {
  const byRepo = catalogImagesByRepo();
  const changed: string[] = [];
  const out = yml.split('\n').map((line) => {
    const m = /^(\s+image:[ \t]*)(\S+)([ \t]*)$/.exec(line);
    if (!m) return line;
    const [, prefix, image, trailing] = m as unknown as [
      string,
      string,
      string,
      string,
    ];
    const current = byRepo.get(imageRepo(image));
    if (current === undefined || current === image) return line;
    changed.push(`${image} → ${current}`);
    return `${prefix}${current}${trailing}`;
  });
  return { yml: out.join('\n'), changed };
}

export async function runUpgrade(opts: UpgradeOptions): Promise<number> {
  const home = opts.monocerosHome ?? defaultMonocerosHome();
  const logger = opts.logger ?? consola;
  const fetchVersions = opts.fetchVersions ?? fetchRuntimeVersions;

  if (opts.list) {
    const versions = await fetchVersions();
    if (versions.length === 0) {
      logger.info('No published runtime versions found.');
      return 0;
    }
    logger.info(
      `Available runtime versions (latest last):\n  ${versions.join('\n  ')}`,
    );
    return 0;
  }

  if (opts.version !== undefined) {
    if (!opts.name) {
      throw new Error(
        'A specific version can only be pinned for one container: `monoceros upgrade <name> <version>`.',
      );
    }
    if (!VERSION_RE.test(opts.version)) {
      throw new Error(
        `Invalid version ${JSON.stringify(opts.version)}. Expected an exact version like '1.1.0'.`,
      );
    }
  }

  if (opts.name && !existsSync(containerConfigPath(opts.name, home))) {
    throw new Error(
      `No such config: ${containerConfigPath(opts.name, home)}. Run \`monoceros init <template> ${opts.name}\` first.`,
    );
  }

  const apply = opts.applyRunner ?? runApply;
  const exec = opts.dockerExec ?? spawnDocker;
  const now = opts.now ?? new Date();

  // `upgrade <name>` targets that one container explicitly (the builder named
  // it, so applying/starting it is intended). Bare `upgrade` is global, but
  // must refresh ONLY currently-running containers — re-applying every config
  // would start stopped containers and materialize never-applied ones, which
  // is an unwanted side effect. Stopped / not-yet-built configs are skipped
  // with a hint. (Prune + the staleness timestamp stay global either way.)
  let targets: string[];
  const skipped: string[] = [];
  if (opts.name) {
    targets = [opts.name];
  } else {
    targets = [];
    for (const name of await listContainerNames(home)) {
      if (await isWorkspaceRunning(containerDir(name, home), exec)) {
        targets.push(name);
      } else {
        skipped.push(name);
      }
    }
  }

  // Prune stale images + stamp the run. Prune stays silent here (no logger) —
  // the upgrade summary reports the result instead, so it's always visible.
  const pruneAndStamp = async (): Promise<{
    removed: number;
    attempted: number;
  }> => {
    const result = await pruneStaleImages({
      home,
      currentContainerNames: new Set(await listContainerNames(home)),
      exec,
    });
    await markUpgraded(now.toISOString(), home);
    return result;
  };

  if (targets.length === 0) {
    logger.info(
      opts.name
        ? 'Nothing to upgrade.'
        : skipped.length > 0
          ? `No running containers to refresh (${skipped.length} not running: ${skipped.join(', ')}). ` +
            'Start one, or refresh it with `monoceros upgrade <name>`.'
          : 'No running containers to refresh.',
    );
    await pruneAndStamp();
    return 0;
  }

  // The base version to pin: an explicit one (per-container only), else the
  // latest published. `setRuntimeVersion` only rewrites the yml when it
  // actually changes, so an already-latest base is left untouched ("only when
  // a newer version exists").
  let pinVersion = opts.version;
  if (pinVersion === undefined) {
    const versions = await fetchVersions();
    pinVersion = versions[versions.length - 1];
    if (!pinVersion) {
      throw new Error('Could not determine the latest runtime version.');
    }
    logger.info(`Latest published runtime version: ${pinVersion}`);
  }

  let worstExit = 0;
  let bumped = 0;
  let retagged = 0;
  for (const name of targets) {
    const ymlPath = containerConfigPath(name, home);
    if (!existsSync(ymlPath)) continue; // removed mid-run — skip
    const raw = await fs.readFile(ymlPath, 'utf8');
    const withRuntime = setRuntimeVersion(raw, pinVersion);
    if (withRuntime !== raw) {
      bumped += 1;
      logger.info(`Pinned '${name}' to runtime ${pinVersion}.`);
    }
    // Curated service images move with the catalog, not with the
    // container (ADR 0052) — a security fix in an upstream image reaches
    // an existing workbench only if upgrade writes the new tag here.
    const { yml: updated, changed } = setCuratedServiceImages(withRuntime);
    for (const line of changed) {
      retagged += 1;
      logger.info(`Retagged a service in '${name}': ${line}.`);
    }
    if (updated !== raw) await fs.writeFile(ymlPath, updated);
    logger.info(`Refreshing '${name}' (rebuild — latest tools)…`);
    const result = await apply({
      name,
      cliVersion: opts.cliVersion,
      monocerosHome: home,
      rebuild: true,
    });
    if (result.containerExitCode !== 0) {
      worstExit = result.containerExitCode;
      logger.warn?.(
        `Upgrade of '${name}' failed (exit ${result.containerExitCode}).`,
      );
    }
  }

  // Prune stale images + stamp the run only when every target succeeded, so a
  // failed refresh keeps nudging instead of looking done. Then report what
  // happened — upgrade is otherwise silent about the prune and the timestamp.
  if (worstExit === 0) {
    const prune = await pruneAndStamp();
    logger.success(
      `Upgraded ${opts.name ? `'${opts.name}'` : `${targets.length} running container${targets.length === 1 ? '' : 's'}`}\n` +
        `  tools     rebuilt — latest pulled\n` +
        `  base      ${pinVersion} ${bumped > 0 ? `(${bumped} bumped)` : '(already latest)'}\n` +
        `  services  images re-pulled${retagged > 0 ? ` (${retagged} retagged)` : ''}\n` +
        `  pruned    ${formatPruneLine(prune)}\n` +
        (skipped.length > 0
          ? `  skipped   ${skipped.length} not running (${skipped.join(', ')})\n`
          : '') +
        `  recorded  ${now.toISOString().slice(0, 16).replace('T', ' ')} UTC`,
    );
  }
  return worstExit;
}

/** Human-readable prune outcome for the upgrade summary. */
function formatPruneLine(prune: {
  removed: number;
  attempted: number;
}): string {
  const gone = prune.attempted - prune.removed;
  if (prune.attempted === 0) return 'nothing stale';
  if (prune.removed === 0) {
    return `0 removed (${gone} stale ${gone === 1 ? 'entry' : 'entries'} already gone)`;
  }
  const base = `${prune.removed} image${prune.removed === 1 ? '' : 's'} removed`;
  return gone > 0
    ? `${base}, ${gone} stale ${gone === 1 ? 'entry' : 'entries'} cleared`
    : base;
}

/** Container config names (`<name>.yml` → `<name>`) under the home. */
async function listContainerNames(home: string): Promise<string[]> {
  try {
    const entries = await fs.readdir(containerConfigsDir(home));
    return entries
      .filter((e) => e.endsWith('.yml'))
      .map((e) => e.slice(0, -'.yml'.length));
  } catch {
    return [];
  }
}
